import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import { useVideoCallContext } from "contexts/VideoCallContext";
import { useVoiceCallContext } from "contexts/VoiceCallContext";
import { useCallFeatures } from "utils/hooks";
import { useCurrentPatient } from "utils/hooks/useCurrentPatient";

export const useCallOptions = () => {
  const { profile } = useProfileContext();
  const { patient } = useCurrentPatient();
  const { voiceCallData, setVoiceCallData } = useVoiceCallContext();
  const { callData: videoCallData, setCallData } = useVideoCallContext();
  const { videoCallsEnabled, voiceCallsEnabled, phoneCallsEnabled } = useCallFeatures();

  invariant(profile);
  invariant(patient);

  const disabled = !!videoCallData || !!voiceCallData;

  const startVoiceCall = () => {
    if (disabled) return;
    setVoiceCallData({
      caller: {
        id: profile.id,
      },
      callee: {
        id: patient.id,
        name: `${patient.first_name} ${patient.last_name}`,
      },
    });
  };

  const startVideoCall = () => {
    if (disabled) return;
    setCallData({
      caller: {
        id: profile.id,
        name: `${profile.first_name} ${profile.last_name}`,
      },
      callee: {
        id: patient.id,
        name: `${patient.first_name} ${patient.last_name}`,
      },
    });
  };

  const startPhoneCall = () => {
    if (disabled) return;
    setVoiceCallData({
      caller: {
        id: profile.id,
      },
      callee: {
        id: patient.id,
        name: `${patient.first_name} ${patient.last_name}`,
        phone_number: patient.phone_number,
      },
    });
  };

  const callOptions = [
    {
      type: "voice" as const,
      enabled: voiceCallsEnabled,
      handler: startVoiceCall,
    },
    {
      type: "video" as const,
      enabled: videoCallsEnabled,
      handler: startVideoCall,
    },
    {
      type: "phone" as const,
      enabled: phoneCallsEnabled,
      handler: startPhoneCall,
    },
  ];

  const enabledOptions = callOptions.filter(opt => opt.enabled);

  return {
    enabledOptions,
    disabled,
    hasMultipleOptions: enabledOptions.length > 1,
    hasSingleOption: enabledOptions.length === 1,
    hasNoOptions: enabledOptions.length === 0,
  };
};
